import { useId, useState } from 'react'

import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { PillCheckbox } from '@/components/ui/pill-checkbox'
import { cn } from '@/utils/tw-helpers'

import { QuestionProps, TChoiceStyle } from './types'

interface MultipleChoicesQuestionProps extends QuestionProps {
  defaultValue?: string[]
  labelClassName?: string
  choiceStyle?: TChoiceStyle
}

export const MultipleChoicesQuestion = ({
  question,
  defaultValue,
  labelClassName,
  onValueChange,
  choiceStyle = 'normal',
}: MultipleChoicesQuestionProps): JSX.Element => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(defaultValue || [])
  const id = useId()

  const handleOptionToggle = (option: string) => {
    const isSelected = selectedOptions.includes(option)
    const newValue = isSelected
      ? selectedOptions.filter((selected) => selected !== option)
      : [...selectedOptions, option]
    setSelectedOptions(newValue)
    onValueChange?.({
      value: newValue.join(','),
    })
  }

  const handleOptionCheckedChange = (option: string, checked: boolean) => {
    const isSelected = selectedOptions.includes(option)
    if (checked && isSelected) return
    const newValue = checked
      ? [...selectedOptions, option]
      : selectedOptions.filter((selected) => selected !== option)
    setSelectedOptions(newValue)
    onValueChange?.({
      value: newValue.join(','),
    })
  }

  const isPillStyle = choiceStyle === 'pill'

  return (
    <section>
      <h4 className={cn('mb-4 text-[13px] font-bold text-secondary-20', labelClassName)}>
        {question.question}
        {question.required && <span className="text-danger"> ※</span>}
      </h4>
      <div
        role="group"
        aria-label={question.question}
        className={cn(isPillStyle ? 'flex flex-wrap gap-2.5' : 'flex flex-col gap-3')}
      >
        {question.surveyQuestionChoices?.map((choice, index) => (
          <div key={choice.id}>
            {isPillStyle ? (
              <PillCheckbox
                id={`${id}-option-${index}`}
                value={choice.id}
                checked={selectedOptions.includes(choice.id)}
                onChange={() => handleOptionToggle(choice.id)}
              >
                {choice.choiceText}
              </PillCheckbox>
            ) : (
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${id}-option-${index}`}
                  checked={selectedOptions.includes(choice.id)}
                  onCheckedChange={(checked) =>
                    handleOptionCheckedChange(choice.id, checked === true)
                  }
                />
                <Label
                  htmlFor={`${id}-option-${index}`}
                  className="text-sm font-medium text-primary-text"
                >
                  {choice.choiceText}
                </Label>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}
